/**
 * Apply events, newest first, as the clawlets reported them: phase, outcome and message. The
 * events of one apply are grouped under their revision, so a rollback reads as one story.
 */
import { LitElement, html } from 'lit';

import { PHASE } from '../i18n/labels.js';
import { pageStyles } from '../../styles/shared/page.js';
import { phaseStatusBadge } from './plane-badge.js';
import { when } from './time.js';
import { planeEventListStyles } from '../../styles/components/plane-event-list.js';

/**
 * The events of each instance and revision together, in the order of their newest event: the
 * phases of two applies reported at the same time on two instances do not interleave.
 */
export function groupEvents(events) {
  const groups = new Map();
  for (const event of events) {
    const key = `${event.instance} ${event.revisionId}`;
    if (!groups.has(key)) {
      groups.set(key, { instance: event.instance, revisionId: event.revisionId, revisionNumber: event.revisionNumber, events: [] });
    }
    groups.get(key).events.push(event);
  }
  return [...groups.values()];
}

class PlaneEventList extends LitElement {
  static properties = {
    events: { type: Array },
    /** Whether to name the instance of each group: on for lists across instances. */
    showInstance: { type: Boolean, attribute: 'show-instance' },
    /** At most this many applies; all of them when unset. */
    limit: { type: Number },
  };

  static styles = [
    pageStyles,
    planeEventListStyles,
  ];

  constructor() {
    super();
    this.events = [];
    this.showInstance = false;
  }

  render() {
    if (!this.events?.length) {
      return html`<p class="empty">No apply reported yet.</p>`;
    }
    return html`
      <ol>
        ${groupEvents(this.events).slice(0, this.limit ?? Infinity).map(
          (group) => html`
            <li class="group">
              <div class="group-head">
                ${this.showInstance ? html`<a href="/workspaces/${group.instance}"><code>${group.instance}</code></a>` : null}
                ${group.revisionId
                  ? html`<a href="/workspaces/${group.instance}/revisions/${group.revisionId}">Revision ${group.revisionNumber}</a>`
                  : html`<span class="muted">No revision</span>`}
                <span class="at">${when(group.events[0].at)}</span>
              </div>
              <ol>
                ${group.events.map(
                  (event) => html`
                    <li class="event">
                      <span>${PHASE[event.phase] ?? event.phase}</span>
                      <span>${phaseStatusBadge(event.status)}</span>
                      <span class="message">${event.message ?? ''}</span>
                      <span class="at">${when(event.at)}</span>
                    </li>
                  `,
                )}
              </ol>
            </li>
          `,
        )}
      </ol>
    `;
  }
}

customElements.define('plane-event-list', PlaneEventList);
